import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ListChecks } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AppShell from "@/components/platform/AppShell";
import { StateBadge } from "@/components/platform/Badges";
import { AuditTimeline } from "@/components/platform/AuditTimeline";
import { DueLabel, Panel, StageBadge, TypeBadge } from "@/components/rights/RightsBadges";
import { StageStepper } from "@/components/rights/StageStepper";
import { VerificationPanel } from "@/components/rights/VerificationPanel";
import { SystemsPanel } from "@/components/rights/SystemsPanel";
import { ResponsePanel } from "@/components/rights/ResponsePanel";
import { EvidencePanel } from "@/components/rights/EvidencePanel";
import { ClosurePanel } from "@/components/rights/ClosurePanel";
import { RequestSummary } from "@/components/rights/RequestSummary";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { apiError, fmtD } from "@/lib/platform";
import { useDirectory, useRightsMeta } from "@/lib/rights";

const TAB_FOR_STAGE = { RECEIVED: "verification", VERIFICATION: "verification", LOCATE_DATA: "systems", REVIEW: "systems", RESPOND: "response", CLOSED: "evidence" };

const TaskList = ({ tasks }) => (
  <Panel eyebrow="Task engine" title={`Linked tasks (${tasks.length})`} testId="request-tasks">
    {tasks.length === 0 ? <div className="text-xs text-slate-500">Assign a system owner to create a confirmation task.</div> : (
      <ul className="space-y-2">
        {tasks.map((t) => (
          <li key={t.id} className="flex items-center gap-2 text-xs" data-testid={`request-task-${t.id}`}>
            <ListChecks className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <Link to={`/tasks?task=${t.id}`} className="flex-1 min-w-0 truncate text-[#008080] hover:underline">{t.task_ref} · {t.title}</Link>
            <span className="text-slate-500 hidden sm:inline">{t.owner}</span>
            <StateBadge state={t.status} />
          </li>
        ))}
      </ul>
    )}
  </Panel>
);

export default function RightsRequestDetail() {
  const { rid } = useParams();
  const auth = useAuth();
  const { meta } = useRightsMeta();
  const users = useDirectory();
  const [r, setR] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState(null);
  const canEdit = auth.can("requests", "edit");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/rights/${rid}`);
      setR(data);
      setTab((t) => t || TAB_FOR_STAGE[data.status]);
    } catch (e) { setErr(apiError(e, "Could not load request")); }
  }, [rid]);
  useEffect(() => { load(); }, [load]);

  if (err) return <AppShell testId="rights-detail-page"><div className="text-sm text-red-600" data-testid="rights-detail-error">{err}</div></AppShell>;
  if (!r || !meta) return <AppShell testId="rights-detail-page"><div className="h-40 rounded-xl bg-slate-100 animate-pulse" /></AppShell>;
  const pending = r.approvals.find((a) => a.status === "Pending");

  return (
    <AppShell testId="rights-detail-page">
      <Link to="/rights" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#003366]" data-testid="rights-back-link">
        <ArrowLeft className="w-3.5 h-3.5" /> Rights Requests
      </Link>
      <div className="flex items-end justify-between flex-wrap gap-4 rr-rise">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-slate-500" data-testid="rights-detail-ref">{r.request_id}</span>
            <TypeBadge type={r.request_type} label={r.request_type_label} testId="rights-detail-type" />
            <StageBadge stage={r.status} label={r.status_label} testId="rights-detail-stage" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-[#003366] mt-1.5" data-testid="rights-detail-subject">{r.subject_name}</h1>
          <p className="text-sm text-slate-600 mt-1">
            {r.subject_type_label} · <span className="font-mono">{r.linked_person_key}</span> · received {fmtD(r.received_date)}
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Target {fmtD(r.target_date)}</div>
          <div className="text-lg"><DueLabel r={r} testId="rights-detail-due" /></div>
        </div>
      </div>

      <StageStepper r={r} meta={meta} canEdit={canEdit} pendingApproval={pending} onChanged={load} />

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <div className="xl:col-span-8 min-w-0">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-white border border-slate-200" data-testid="rights-detail-tabs">
              <TabsTrigger value="verification" data-testid="tab-verification">Verification</TabsTrigger>
              <TabsTrigger value="systems" data-testid="tab-systems">Data map ({r.systems.length})</TabsTrigger>
              <TabsTrigger value="response" data-testid="tab-response">Response</TabsTrigger>
              <TabsTrigger value="evidence" data-testid="tab-evidence">Evidence ({r.evidence.length})</TabsTrigger>
              <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
            </TabsList>
            <TabsContent value="verification"><VerificationPanel r={r} canEdit={canEdit} onChanged={load} /></TabsContent>
            <TabsContent value="systems"><SystemsPanel r={r} meta={meta} users={users} canEdit={canEdit} onChanged={load} /></TabsContent>
            <TabsContent value="response"><ResponsePanel r={r} meta={meta} canEdit={canEdit} onChanged={load} /></TabsContent>
            <TabsContent value="evidence"><EvidencePanel r={r} meta={meta} canEdit={canEdit} onChanged={load} /></TabsContent>
            <TabsContent value="activity">
              <Panel eyebrow="Universal audit log" title="Activity" testId="rights-activity">
                <AuditTimeline items={r.activity} testId="rights-audit-timeline" />
              </Panel>
            </TabsContent>
          </Tabs>
        </div>
        <aside className="xl:col-span-4 space-y-6">
          <ClosurePanel r={r} canEdit={canEdit} onChanged={load} />
          <RequestSummary key={`${r.owner}-${r.target_date}`} r={r} meta={meta} users={users} canEdit={canEdit} onChanged={load} />
          <TaskList tasks={r.tasks} />
        </aside>
      </div>
    </AppShell>
  );
}
